<template>
  <ZDialog v-model:open="isDialogOpen">
    <ZDialogContent class="lg:max-w-[625px] lg:overflow-y-hidden max-w-screen-lg overflow-y-scroll h-full lg:h-auto">
      <ZDialogHeader>
        <ZDialogTitle>{{
          exportType === 'assets' ? $t('report.label.generate_export') : $t('report.label.generate_report')
        }}</ZDialogTitle>
        <ZDialogDescription>
          {{ exportType === 'assets' ? $t('report.message.generate_export') : $t('report.message.generate_report') }}
        </ZDialogDescription>
      </ZDialogHeader>
      <template v-if="state == 'loading'">
        <IconAlert type="loading">
          {{ $t('common.action.loading') }}
        </IconAlert>
      </template>
      <template v-else-if="state == 'loaded'">
        <div class="lg:max-h-[70vh] lg:overflow-auto">
          <FormValidator ref="form" :errors="errors">
            <GenerateReportForm
              :link-data="linkData"
              :categories="categories"
              :tags="tags"
              :statuses="assetStatuses"
            ></GenerateReportForm>
          </FormValidator>
        </div>
        <ZDialogFooter>
          <div class="w-full flex flex-col items-end">
            <template v-if="taskState == ''">
              <ZButton class="mt-2" @click="generateUrlLink()">
                {{ exportType === 'assets' ? $t('report.command.export_data') : $t('report.command.generate') }}
              </ZButton>
            </template>
            <template v-else-if="taskState == 'pending'">
              Przygotowywanie eksportu: przetworzono {{ taskProgress }} zasobów
            </template>
            <template v-else-if="taskState == 'done'">
              Eksport zakończono
            </template>
          </div>
        </ZDialogFooter>
      </template>
      <template v-else-if="state == 'error'">
        <IconAlert type="error">
          {{ $t('common.errors.unexpected') }}
        </IconAlert>
      </template>
    </ZDialogContent>
  </ZDialog>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue';
import {
  ZDialog,
  ZDialogContent,
  ZDialogDescription,
  ZDialogFooter,
  ZDialogHeader,
  ZDialogTitle,
} from '@shadcn/components/ui/dialog';
import FormValidator from '@ui/FormValidator.vue';
import IconAlert from '@ui/IconAlert.vue';
import { ComponentLoadState } from '@/types/ComponentLoadState';
import { ZButton } from '@shadcn/components/ui/button';
import {downloadReport, generateAssetsReportFileUrl, getTaskInfo} from '@/api/report';
import { getCompactCategories } from '@/api/category';
import CategoryCompactData = App.Data.CategoryCompactData;
import GenerateReportForm from '@ui/Reports/GenerateAssetsReportForm.vue';
import { getTagsCompact } from '@/api';
import { mapState } from '@modules/pinia';
import useUserStore from '@/stores/auth';
import ExtensionType = App.Enums.ExtensionType;
import { HttpStatusCode } from '@modules/axios';
import { ValidationErrors } from '@/types/ValidationErrors';
import { KeyValue } from '@/types/Common';
import TagsCompactData = App.Data.TagsCompactData;
import symbolsStore from '@/stores/symbols';
import {poolTask} from "@/api/BackgroundTask";

interface linkDataInterface {
  category_id?: string;
  with_custom_attributes?: number;
  with_gallery?: boolean;
  extension: ExtensionType;
  tags?: any;
  status?: string;
  scope?: string;
}

export default defineComponent({
  components: {
    GenerateReportForm,
    ZButton,
    IconAlert,
    FormValidator,
    ZDialog,
    ZDialogContent,
    ZDialogDescription,
    ZDialogFooter,
    ZDialogHeader,
    ZDialogTitle,
  },

  props: {
    exportType: {
      type: String,
      required: true,
    },
  },

  emits: ['saved'],

  setup() {
    return {
      toast: inject('toast') as (props: any) => void,
    };
  },

  data() {
    return {
      isDialogOpen: true,
      state: 'loading' as ComponentLoadState,
      loader: null as any,
      url: '',
      categories: {} as CategoryCompactData,
      errors: null as null | ValidationErrors,
      tags: {},
      assetStatuses: {} as KeyValue,
      taskState: '' as string,
      taskProgress: 0 as number,
      linkData: {
        category_id: '',
        with_custom_attributes: 0 as number,
        with_gallery: false as boolean,
        extension: 'xlsx' as ExtensionType,
        tags: [],
        status: 'all',
        scope: 'full',
      } as linkDataInterface,
    };
  },

  computed: {
    ...mapState(useUserStore, ['user']),
  },

  watch: {
    isDialogOpen() {
      if (!this.isDialogOpen) {
        this.$router.back();
      }
    },
  },

  async mounted() {
    try {
      if (this.exportType === 'models') {
        delete this.linkData.category_id;
        delete this.linkData.tags;
      } else if (this.exportType === 'quantitative') {
        delete this.linkData.category_id;
        delete this.linkData.with_custom_attributes;
        delete this.linkData.tags;
        delete this.linkData.with_gallery;
        delete this.linkData.status;
        delete this.linkData.scope;
      } else {
        delete this.linkData.scope;
        this.getCategories();
      }
      const symbols = symbolsStore();
      this.assetStatuses = await symbols.get('asset_statuses');
      const tagsResponse = await getTagsCompact().execute();
      this.tags = tagsResponse.data.reduce((accumulator: KeyValue, value: TagsCompactData) => {
        return { ...accumulator, [value.id]: value.name };
      }, {});
      this.state = 'loaded';
    } catch (error: any) {
      this.state = 'error';
      throw error;
    }
  },

  methods: {
    async startTask(task: string) {
      this.taskState = 'pending';
      const response = await poolTask(task).execute();
      this.taskProgress = response.data.progress;
      if (response.data.results.url == undefined) {
        setTimeout(() => this.startTask(task), 3000);
      } else {
        downloadReport('..' + response.data.results.url, this.linkData.extension, response.data.results.filename);
        this.taskState = 'done';
      }
    },

    generateUrlLink() {
      this.errors = null;
      if (this.exportType === 'models') {
        generateAssetsReportFileUrl({
          export_type: this.exportType,
          scope: this.linkData.scope,
          status: this.linkData.status,
          extension: this.linkData.extension,
          with_custom_attributes: this.linkData.with_custom_attributes,
          with_gallery: this.linkData.with_gallery,
        })
          .execute()
          .then(res => {
            getTaskInfo(res.data.link).execute().then(res2 => {
              this.startTask(res2.data.task);
            });
          })
          .catch(error => {
            if (error?.response?.status == HttpStatusCode.UnprocessableEntity) {
              this.errors = error.response.data.errors;
            }
          });
      } else if (this.exportType === 'quantitative') {
        generateAssetsReportFileUrl({
          export_type: this.exportType,
          extension: this.linkData.extension,
        })
          .execute()
          .then(res => {
            downloadReport(res.data.link, this.linkData.extension);
          })
          .catch(error => {
            if (error?.response?.status == HttpStatusCode.UnprocessableEntity) {
              this.errors = error.response.data.errors;
            }
          });
      } else if (this.exportType === 'assets') {
        generateAssetsReportFileUrl({
          export_type: this.exportType,
          extension: this.linkData.extension,
          category_id: this.linkData.category_id,
          with_custom_attributes: this.linkData.with_custom_attributes,
          with_gallery: this.linkData.with_gallery,
          tags: this.linkData.tags,
          status: this.linkData.status,
        })
          .execute()
          .then(res => {
            downloadReport(res.data.link, this.linkData.extension);
          })
          .catch(error => {
            if (error?.response?.status == HttpStatusCode.UnprocessableEntity) {
              this.errors = error.response.data.errors;
            }
          });
      }
    },

    async getCategories() {
      const categoriesResponse = await getCompactCategories().execute();
      this.categories = categoriesResponse.data;
    },
  },
});
</script>
